import { Heading, Card } from '@chakra-ui/react';
import { formatCurrency } from '@monetize/utils/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import {
  checkRevenueJournalReady,
  checkRevenueScheduleReady,
  useDownloadRevenueJournalCsv,
  useDownloadRevenueScheduleCsv,
} from '../../../api/cpqService';
import { useSearchRevenueSchedules } from '../../../api/revenueService';
import { ROUTES } from '../../../constants';
import { useACL } from '../../../services/acl/acl';
import { useFlags } from '../../../services/launchDarkly';
import { logger } from '../../../services/logger';
import { IQuoteRespSchema, QuoteStatusEnum } from '../../../types';
import { downloadBlobAsFile } from '../../../utils/download';
import {
  MBox,
  MButton,
  MCard,
  MFlex,
  MIcon,
  MLink,
  MText,
} from '../../Monetize';
import { MSpinner } from '../../Monetize/chakra';

const POLL_INITIAL_INTERVAL_MS = 2_000;
const POLL_STEP_MS = 5_000;
const POLL_MAX_INTERVAL_MS = 30_000;

function useIncrementalPolling(
  checkFn: () => Promise<boolean>,
  enabled: boolean,
  resetKey?: unknown,
): boolean {
  const [isReady, setIsReady] = useState(false);
  const intervalRef = useRef<number>(POLL_INITIAL_INTERVAL_MS);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkFnRef = useRef(checkFn);
  checkFnRef.current = checkFn;

  useEffect(() => {
    setIsReady(false);
  }, [resetKey]);

  useEffect(() => {
    if (!enabled || isReady) {
      return;
    }

    intervalRef.current = POLL_INITIAL_INTERVAL_MS;

    const getNextInterval = () => {
      // eslint-disable-next-line prefer-destructuring
      const current = intervalRef.current;
      intervalRef.current = Math.min(
        current + POLL_STEP_MS,
        POLL_MAX_INTERVAL_MS,
      );
      return current;
    };

    let cancelled = false;

    const poll = async () => {
      try {
        const ready = await checkFnRef.current();
        if (cancelled) {
          return;
        }
        if (ready) {
          setIsReady(true);
          return;
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        logger.error(error);
      }

      timeoutRef.current = setTimeout(poll, getNextInterval());
    };

    timeoutRef.current = setTimeout(poll, intervalRef.current);

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, isReady]);

  return isReady;
}

const QuoteAnalyticsRevenueCard = ({ quote }: { quote: IQuoteRespSchema }) => {
  const { canDo } = useACL();
  const { enableRevRecV2 } = useFlags();
  const canUpdateSales = canDo([['sales', 'update']]);

  const isActive =
    quote.status === QuoteStatusEnum.PROCESSED &&
    canUpdateSales &&
    enableRevRecV2;

  const isRevenueScheduleReady = useIncrementalPolling(
    () => checkRevenueScheduleReady(quote.id),
    isActive,
    quote.id,
  );

  const isRevenueJournalReady = useIncrementalPolling(
    () => checkRevenueJournalReady(quote.id),
    isActive,
    quote.id,
  );

  const { data: revenueSchedulesData, isLoading: isLoadingRevenueSchedules } =
    useSearchRevenueSchedules(
      { quoteId: quote.id },
      {
        enabled: isActive && enableRevRecV2 && !!quote.id,
        refetchOnWindowFocus: false,
      },
    );
  const revenueContracts = useMemo(() => {
    interface RevenueContractEntry {
      id: string;
      version: number;
      amount: number | null;
      termStart: string | null;
      termEnd: string | null;
    }
    const latestByContractId = new Map<string, RevenueContractEntry>();
    for (const schedule of revenueSchedulesData?.content ?? []) {
      const contractId = schedule.references?.revenueContractId;
      if (!contractId) {
        continue;
      }
      const incomingVersion = schedule.version ?? -Infinity;
      const existing = latestByContractId.get(contractId);
      if (existing && existing.version >= incomingVersion) {
        continue;
      }
      latestByContractId.set(contractId, {
        id: contractId,
        version: incomingVersion,
        amount: schedule.contractData?.amount ?? null,
        termStart: schedule.contractData?.term?.start ?? null,
        termEnd: schedule.contractData?.term?.endInclusive ?? null,
      });
    }
    return Array.from(latestByContractId.values());
  }, [revenueSchedulesData]);

  const {
    mutateAsync: downloadRevenueScheduleCsv,
    isPending: isDownloadingRevenueSchedule,
  } = useDownloadRevenueScheduleCsv({ meta: { showErrorToast: true } });

  const {
    mutateAsync: downloadRevenueJournalCsv,
    isPending: isDownloadingRevenueJournal,
  } = useDownloadRevenueJournalCsv({ meta: { showErrorToast: true } });

  const handleDownloadRevenueSchedule = async () => {
    try {
      const result = await downloadRevenueScheduleCsv({ quoteId: quote.id });

      downloadBlobAsFile(result.data, result.filename, 'text/csv');
    } catch (error) {
      logger.error(error);
    }
  };
  const handleDownloadRevenueJournal = async () => {
    try {
      const result = await downloadRevenueJournalCsv({ quoteId: quote.id });

      downloadBlobAsFile(result.data, result.filename, 'text/csv');
    } catch (error) {
      logger.error(error);
    }
  };

  return (
    isActive && (
      <MBox>
        {enableRevRecV2 && (
          <MCard variant="borderless" mt="2">
            <Card.Body>
              <Heading as="h2" size="md" mb={4}>
                Revenue Contract
              </Heading>
              {isLoadingRevenueSchedules ? (
                <MSpinner size="sm" />
              ) : revenueContracts.length > 0 ? (
                <MFlex direction="column" gap={2}>
                  {revenueContracts.map((contract) => {
                    return (
                      <MLink
                        key={contract.id}
                        href={ROUTES.getRevenueContractDetailRoute(contract.id)}
                        fontSize="sm"
                        display="flex"
                        flexDirection="column"
                      >
                        <MText
                          as="span"
                          fontWeight="semibold"
                          color="inherit"
                          display="flex"
                          alignItems="center"
                        >
                          {contract.id}
                          <MText as="span" color="inherit" ml="1">
                            {contract.amount !== null &&
                              formatCurrency(contract.amount)}
                          </MText>
                        </MText>
                      </MLink>
                    );
                  })}
                </MFlex>
              ) : (
                <MText
                  fontStyle="italic"
                  color="tGray.darkPurple"
                  fontSize="sm"
                >
                  Revenue contract is not created yet.
                </MText>
              )}
            </Card.Body>
          </MCard>
        )}
        <MCard variant="borderless" mt="2">
          <Card.Body>
            <Heading as="h2" size="md" mb={4}>
              Revenue Schedule
            </Heading>
            <MFlex
              direction="column"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <MButton
                isLoading={isDownloadingRevenueSchedule}
                loadingText="Downloading Revenue Schedule"
                variant="primary"
                mb={2}
                isDisabled={!isRevenueScheduleReady}
                onClick={handleDownloadRevenueSchedule}
              >
                <MIcon as={MdOutlineFileDownload} mr={2} />
                Download Revenue Schedule
              </MButton>
            </MFlex>
          </Card.Body>
        </MCard>
        <MCard variant="borderless" mt="2">
          <Card.Body>
            <Heading as="h2" size="md" mb={4}>
              Revenue Journal
            </Heading>
            <MFlex>
              <MButton
                isLoading={isDownloadingRevenueJournal}
                loadingText="Downloading Revenue Journal"
                variant="primary"
                isDisabled={!isRevenueJournalReady}
                onClick={handleDownloadRevenueJournal}
              >
                <MIcon as={MdOutlineFileDownload} mr={2} />
                Download Revenue Journal
              </MButton>
            </MFlex>
          </Card.Body>
        </MCard>
      </MBox>
    )
  );
};

export default QuoteAnalyticsRevenueCard;
